/**
 * Lobby themes: the nav pages after Tables (owner 2026-10-20: "Sports nav should have the sports ones showing like golf
 * basketball", plus Classics, Asian, Fantasy & adventure and Sci-fi & heroes). Games are matched by skin id (falling back
 * to the title id), so a new title appears on a theme page once its skin is added here; every game stays in the Lobby.
 */
export interface ThemeGroup {
  id: string;
  label: string;
  skins: string[];
}

export interface Theme {
  id: string;
  path: string;
  /** short nav label */
  nav: string;
  title: string;
  intro: string;
  /** sub-tabs (Sports: one per sport), in order; themes without groups list `skins` */
  groups?: ThemeGroup[];
  skins?: string[];
  /** table games shown on the page too, by game number (e.g. 34 = tg34-andar-bahar) */
  tables?: { title: string; games: number[] };
}

export const THEMES: Theme[] = [
  {
    id: 'sports',
    path: '/sports',
    nav: 'Sports',
    title: 'Sports',
    intro: 'Slots from the golf course, the court, the pitch and the river bank.',
    groups: [
      { id: 'golf', label: 'Golf', skins: ['fairway-fury'] },
      { id: 'basketball', label: 'Basketball', skins: ['buzzer-beater'] },
      { id: 'football', label: 'Football', skins: ['goal-rush'] },
      { id: 'afl', label: 'AFL', skins: ['high-mark'] },
      { id: 'rugby', label: 'Rugby', skins: ['try-time', 'set-of-six', 'highland-charge', 'golden-point'] },
      { id: 'cricket', label: 'Cricket', skins: ['century-cash'] },
      { id: 'fishing', label: 'Fishing', skins: ['tide-and-tackle'] },
    ],
  },
  {
    id: 'classics',
    path: '/classics',
    nav: 'Classics',
    title: 'Classics',
    intro: 'Sevens, fruit, pots of gold and hold-and-win jackpots.',
    skins: ['sevens-ablaze', 'furnace-sevens', 'shamrock-pot-party', 'moonpearl-oasis'],
  },
  {
    id: 'asian',
    path: '/asian',
    nav: 'Asian',
    title: 'Asian',
    intro: 'Lions, lanterns, koi and thunder spirits.',
    skins: ['jade-lion-ways', 'lantern-lion', 'moonlit-koi-garden', 'thunder-oni', 'prism-parade'],
    // owner 2026-10-22: the Asian tables belong on the Asian page as well
    tables: { title: 'Asian tables', games: [34, 35, 36, 37] },
  },
  {
    id: 'fantasy',
    path: '/fantasy',
    nav: 'Fantasy',
    title: 'Fantasy & adventure',
    intro: 'Star maps, sky forges, sea titans, sun-and-moon duels and the wild west.',
    skins: ['astral-archive', 'ember-orrery', 'skyforge-summit', 'stormforge-citadel', 'zenith-sanctum', 'cinder-throne', 'solstice-rivals', 'ocean-titans', 'tidewell-bazaar', 'sundown-bounty', 'gold-rush-carts'],
  },
  {
    id: 'scifi',
    path: '/sci-fi',
    nav: 'Sci-fi',
    title: 'Sci-fi & heroes',
    intro: 'Labs, space salvage, cyber vaults and robots, plus the everyday heroes on patrol, on site and in class.',
    skins: ['biolab-bloom', 'deep-space-salvage', 'neon-orbit', 'reactor-grid', 'vault-protocol', 'rift-riders', 'clockwork-heist', 'cash-patrol', 'hard-hat-cash', 'top-marks'],
  },
];

/** The skins of a theme (all groups, in order). */
export const themeSkins = (t: Theme): string[] => t.skins ?? (t.groups ?? []).flatMap((g) => g.skins);

/** Does a game belong to these skins? Matched on the game's skin, or its title id when a title shares the skin's id. */
export const inSkins = (g: { skinId: string; titleId: string }, skins: string[]): boolean => skins.includes(g.skinId) || skins.includes(g.titleId);
